import aiBadgeFilledIcon from "@tabler/icons/filled/ai-badge.svg";
import bellFilledIcon from "@tabler/icons/filled/bell.svg";
import bookmarkFilledIcon from "@tabler/icons/filled/bookmark.svg";
import crystalBallFilledIcon from "@tabler/icons/filled/crystal-ball.svg";
import homeFilledIcon from "@tabler/icons/filled/home.svg";
import mailFilledIcon from "@tabler/icons/filled/mail.svg";
import profileFilledIcon from "@tabler/icons/filled/profile.svg";
import settingsFilledIcon from "@tabler/icons/filled/settings.svg";
import sparklesFilledIcon from "@tabler/icons/filled/sparkles.svg";
import usersFilledIcon from "@tabler/icons/filled/users.svg";
import aiBadgeIcon from "@tabler/icons/outline/ai-badge.svg";
import bellIcon from "@tabler/icons/outline/bell.svg";
import bookmarkIcon from "@tabler/icons/outline/bookmark.svg";
import crystalBallIcon from "@tabler/icons/outline/crystal-ball.svg";
import homeIcon from "@tabler/icons/outline/home.svg";
import mailIcon from "@tabler/icons/outline/mail.svg";
import profileIcon from "@tabler/icons/outline/profile.svg";
import rocketIcon from "@tabler/icons/outline/rocket.svg";
import searchIcon from "@tabler/icons/outline/search.svg";
import settingsIcon from "@tabler/icons/outline/settings.svg";
import sparklesIcon from "@tabler/icons/outline/sparkles.svg";
import usersIcon from "@tabler/icons/outline/users.svg";
import React from "react";
import { FormattedMessage } from "react-intl";

import { useAuth } from "@/contexts/auth/useAuth";
import { useStatContext } from "@/contexts/stat-context";
import { useFeatures, useSelectGroupsTab } from "@/hooks";

import SubscriptionDialog from "../premium/subscription-dialog";

import type { SidebarNavigationLinkProps } from "@/components/sidebar/sidebar-navigation-link";

enum NavigationKeys {
  HOME = "home",
  DISCOVER = "discover",
  ALERTS = "alerts",
  PREDICTIONS = "predict",
  AI_SEARCH = "ai-search",
  SUBSCRIPTION = "subscription",
  CHATS = "chats",
  GROUPS = "groups",
  FEEDS = "feeds",
  BOOKMARKS = "bookmarks",
  BOOSTS = "boosts",
  PROFILE = "profile",
  SETTINGS = "settings",
}

type NavigationLink = SidebarNavigationLinkProps & {
  order: number;
  enabled?: boolean;
};

type NavigationCustomItem = {
  customComponent: React.ReactNode;
  order: number;
  enabled?: boolean;
};

type NavigationItem = NavigationLink | NavigationCustomItem;

type NavigationItems = NonNullable<ReturnType<typeof useNavigationItems>>;

const useNavigationItems = () => {
  const { unreadChatsCount, totalUnreadFeedStatuses, unreadAlertsCount } =
    useStatContext();
  const features = useFeatures();
  const { currentAccount } = useAuth();
  const { initialGroupsRoute } = useSelectGroupsTab();

  if (!currentAccount) return null;

  const items = {
    [NavigationKeys.HOME]: {
      to: "/",
      icon: homeIcon,
      activeIcon: homeFilledIcon,
      text: <FormattedMessage id="tabs_bar.home" defaultMessage="Home" />,
      showIndicator: totalUnreadFeedStatuses > 0,
      order: 0,
    },
    [NavigationKeys.DISCOVER]: {
      to: "/discover",
      icon: searchIcon,
      activeIcon: searchIcon,
      text: (
        <FormattedMessage id="tabs_bar.discover" defaultMessage="Discover" />
      ),
      order: 0,
    },
    [NavigationKeys.ALERTS]: {
      to: "/alerts",
      icon: bellIcon,
      activeIcon: bellFilledIcon,
      count: unreadAlertsCount,
      countMax: 99,
      text: <FormattedMessage id="tabs_bar.alerts" defaultMessage="Alerts" />,
      order: 0,
    },
    [NavigationKeys.PREDICTIONS]: {
      to: "/predictions",
      icon: crystalBallIcon,
      activeIcon: crystalBallFilledIcon,
      text: (
        <FormattedMessage id="tabs_bar.predictions" defaultMessage="Predict" />
      ),
      enabled: features.predictions,
      order: 0,
    },
    [NavigationKeys.AI_SEARCH]: {
      to: "/ai-search",
      icon: aiBadgeIcon,
      activeIcon: aiBadgeFilledIcon,
      text: (
        <FormattedMessage
          id="tabs_bar.ai_search"
          defaultMessage="Truth Search AI"
        />
      ),
      order: 0,
    },
    [NavigationKeys.SUBSCRIPTION]: {
      customComponent: <SubscriptionDialog />,
      order: 0,
    },
    [NavigationKeys.CHATS]: {
      to: "/chats",
      icon: mailIcon,
      activeIcon: mailFilledIcon,
      count: unreadChatsCount,
      countMax: 9,
      text: (
        <FormattedMessage id="navigation.chats" defaultMessage="Messages" />
      ),
      order: 0,
    },
    [NavigationKeys.GROUPS]: {
      to: initialGroupsRoute,
      icon: usersIcon,
      activeIcon: usersFilledIcon,
      text: <FormattedMessage id="tabs_bar.groups" defaultMessage="Groups" />,
      order: 0,
    },
    [NavigationKeys.FEEDS]: {
      to: "/feeds",
      icon: sparklesIcon,
      activeIcon: sparklesFilledIcon,
      text: <FormattedMessage id="tabs_bar.feeds" defaultMessage="Feeds" />,
      order: 0,
    },
    [NavigationKeys.BOOKMARKS]: {
      to: "/bookmarks",
      icon: bookmarkIcon,
      activeIcon: bookmarkFilledIcon,
      text: (
        <FormattedMessage id="tabs_bar.bookmarks" defaultMessage="Bookmarks" />
      ),
      enabled: features.bookmarks,
      order: 0,
    },
    [NavigationKeys.BOOSTS]: {
      to: "/settings/boosts",
      icon: rocketIcon,
      activeIcon: rocketIcon,
      text: (
        <FormattedMessage
          id="tabs_bar.boosts"
          defaultMessage="Boosted Truths"
        />
      ),
      enabled: features.boosts,
      order: 0,
    },
    [NavigationKeys.PROFILE]: {
      to: `/@${currentAccount.acct}`,
      icon: profileIcon,
      activeIcon: profileFilledIcon,
      text: <FormattedMessage id="tabs_bar.profile" defaultMessage="Profile" />,
      order: 0,
    },
    [NavigationKeys.SETTINGS]: {
      to: "/settings/preferences",
      icon: settingsIcon,
      activeIcon: settingsFilledIcon,
      text: (
        <FormattedMessage id="tabs_bar.settings" defaultMessage="Settings" />
      ),
      showIndicator: !currentAccount.source.email_verified,
      order: 0,
    },
  };

  return items;
};

export {
  useNavigationItems,
  type NavigationItem,
  type NavigationItems,
  type NavigationKeys,
};
